import React, { useEffect, useMemo, useCallback, useRef } from "react";
import { useGLTF, useAnimations, useFBX, useTexture } from "@react-three/drei";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import _ from "lodash";
import { AvatarProps } from "../../types";
import { createAnimation } from "../../utils/animation";
import blendDataBlink from "../../assets/animations/blendDataBlink.json";

export const Avatar: React.FC<AvatarProps> = ({
  avatar_url,
  playing,
  clips,
  setMorphTargetDictionaryBody,
  setMorphTargetDictionaryLowerTeeth,
  morphTargetDictionaryBody,
  morphTargetDictionaryLowerTeeth,
  stopAnimationsRef,
}) => {
  const gltf = useGLTF(avatar_url);

  const [
    bodyTexture,
    eyesTexture,
    teethTexture,
    bodySpecularTexture,
    bodyRoughnessTexture,
    bodyNormalTexture,
    teethNormalTexture,
    hairTexture,
    tshirtDiffuseTexture,
    tshirtNormalTexture,
    tshirtRoughnessTexture,
    hairAlphaTexture,
    hairNormalTexture,
    hairRoughnessTexture,
  ] = useTexture([
    "/images/body_new.jpeg",
    "/images/eyes_new.png",
    "/images/teeth_diffuse.webp",
    "/images/body_specular.webp",
    "/images/body_roughness.webp",
    "/images/body_normal.webp",
    "/images/teeth_normal.webp",
    "/images/h_color.webp",
    "/images/tshirt_new.png",
    "/images/tshirt_normal.webp",
    "/images/tshirt_roughness.webp",
    "/images/h_alpha.webp",
    "/images/h_normal.webp",
    "/images/h_roughness.webp",
  ]);

  // Configurazione texture
  const textures = [
    bodyTexture,
    eyesTexture,
    teethTexture,
    teethNormalTexture,
    bodySpecularTexture,
    bodyRoughnessTexture,
    bodyNormalTexture,
    tshirtDiffuseTexture,
    tshirtNormalTexture,
    tshirtRoughnessTexture,
    hairAlphaTexture,
    hairNormalTexture,
    hairRoughnessTexture,
  ];

  textures.forEach((t) => {
    t.flipY = false;
  });

  // Configurazione materiali
  gltf.scene.traverse((node: any) => {
    if (
      node.type === "Mesh" ||
      node.type === "LineSegments" ||
      node.type === "SkinnedMesh"
    ) {
      node.castShadow = true;
      node.receiveShadow = true;
      node.frustumCulled = false;

      if (node.name.includes("Body")) {
        node.castShadow = true;
        node.receiveShadow = true;

        node.material = new THREE.MeshStandardMaterial();
        node.material.map = bodyTexture;
        node.material.roughness = 1.7;
        node.material.roughnessMap = bodyRoughnessTexture;
        node.material.normalMap = bodyNormalTexture;
        node.material.normalScale = new THREE.Vector2(0.6, 0.6);

        setMorphTargetDictionaryBody(node.morphTargetDictionary);

        node.material.envMapIntensity = 0.8;
      }

      if (node.name.includes("Eyes")) {
        node.material = new THREE.MeshStandardMaterial();
        node.material.map = eyesTexture;
        node.material.roughness = 0.1;
        node.material.envMapIntensity = 0.5;
      }

      if (node.name.includes("TeethLower")) {
        setMorphTargetDictionaryLowerTeeth(node.morphTargetDictionary);
      }

      if (node.name.includes("Teeth")) {
        node.receiveShadow = true;
        node.castShadow = true;
        node.material = new THREE.MeshStandardMaterial();
        node.material.roughness = 0.1;
        node.material.map = teethTexture;
        node.material.normalMap = teethNormalTexture;
        node.material.envMapIntensity = 0.7;
      }

      if (node.name.includes("Hair")) {
        node.material = new THREE.MeshStandardMaterial();
        node.material.map = hairTexture;
        node.material.alphaMap = hairAlphaTexture;
        node.material.normalMap = hairNormalTexture;
        node.material.roughnessMap = hairRoughnessTexture;
        node.material.transparent = true;
        node.material.depthWrite = false;
        node.material.side = 2;
        node.material.color.setHex(0x000000);
        node.material.envMapIntensity = 0.3;
      }

      if (node.name.includes("TSHIRT")) {
        node.material = new THREE.MeshStandardMaterial();
        node.material.map = tshirtDiffuseTexture;
        node.material.normalMap = tshirtNormalTexture;
        node.material.roughnessMap = tshirtRoughnessTexture;
        node.material.color.setHex(0xffffff);
        node.material.envMapIntensity = 0.5;
      }
    }
  });

  const mixer = useMemo(
    () => new THREE.AnimationMixer(gltf.scene),
    [gltf.scene]
  );

  // Ref per tenere traccia delle azioni della bocca
  const mouthActionsRef = useRef<THREE.AnimationAction[]>([]);

  // Funzione per fermare solo le animazioni della bocca (non idle e blink)
  const stopMouthAnimations = useCallback(() => {
    // Ferma e resetta solo le animazioni della bocca
    mouthActionsRef.current.forEach((action) => {
      action.stop();
      action.reset();
    });

    // Pulisci l'array
    mouthActionsRef.current = [];
  }, []);

  // Esponi la funzione di stop tramite ref
  useEffect(() => {
    if (stopAnimationsRef) {
      stopAnimationsRef.current = stopMouthAnimations;
    }
  }, [stopAnimationsRef, stopMouthAnimations]);

  // Animazioni idle
  const idleFbx = useFBX("/idle.fbx");
  const { clips: idleClips } = useAnimations(idleFbx.animations);

  idleClips[0].tracks = _.filter(idleClips[0].tracks, (track) => {
    return (
      track.name.includes("Head") ||
      track.name.includes("Neck") ||
      track.name.includes("Spine2")
    );
  });

  idleClips[0].tracks = _.map(idleClips[0].tracks, (track) => {
    if (track.name.includes("Head")) {
      track.name = "head.quaternion";
    }
    if (track.name.includes("Neck")) {
      track.name = "neck.quaternion";
    }
    if (track.name.includes("Spine")) {
      track.name = "spine2.quaternion";
    }
    return track;
  });

  // Inizializzazione animazioni
  useEffect(() => {
    const idleClipAction = mixer.clipAction(idleClips[0]);
    idleClipAction.play();

    const blinkClip = createAnimation(
      blendDataBlink,
      morphTargetDictionaryBody || {},
      "HG_Body"
    );
    if (blinkClip) {
      const blinkAction = mixer.clipAction(blinkClip);
      blinkAction.play();
    }
  }, [mixer, idleClips, morphTargetDictionaryBody]);

  // Riproduzione animazioni quando disponibili
  useEffect(() => {
    if (playing === false) return;
    if (clips.length === 0) {
      console.log("No clips available for animation");
      return;
    }

    // Pulisci le azioni precedenti
    mouthActionsRef.current = [];

    clips.forEach((clip, index) => {
      const clipAction = mixer.clipAction(clip);
      clipAction.setLoop(THREE.LoopOnce, 1);
      clipAction.play();

      // Assicuriamoci che l'animazione sia visibile impostando il peso
      clipAction.setEffectiveWeight(1.0);
      clipAction.setEffectiveTimeScale(1.0);

      // Aggiungi l'azione all'array per poterla fermare in seguito
      mouthActionsRef.current.push(clipAction);
    });
  }, [playing, clips, mixer]);

  // Ottieni invalidate per triggerare il rendering on-demand
  const { invalidate } = useThree();

  // Aggiornamento mixer - richiede invalidate() per frameloop="demand"
  useFrame((state, delta) => {
    if (mixer) {
      mixer.update(delta);
      // Richiede un nuovo frame solo se ci sono animazioni attive
      invalidate();
    }
  });

  // Renderizza la scena nel canvas
  useEffect(() => {
    if (gltf.scene) {
      console.log("Avatar scene loaded");
    }
  }, [gltf.scene]);

  // Debug: log della scena per verificare il caricamento (solo una volta)
  useEffect(() => {
    console.log("Avatar loaded - Scene children:", gltf.scene.children.length);
    console.log("Avatar URL:", avatar_url);

    // Log dettagliato dei children e morph targets
    gltf.scene.traverse((node: any) => {
      if (
        node.morphTargetDictionary &&
        Object.keys(node.morphTargetDictionary).length > 0
      ) {
        console.log(
          `Node ${node.name} has morph targets:`,
          Object.keys(node.morphTargetDictionary).slice(0, 10)
        );
        console.log(
          `  Total morph targets: ${
            Object.keys(node.morphTargetDictionary).length
          }`
        );

        if (node.morphTargetInfluences) {
          console.log(
            `  Current influences: ${node.morphTargetInfluences.slice(0, 10)}`
          );
        }
      }
    });
  }, [avatar_url, gltf.scene]);

  return (
    <group name="avatar">
      <primitive object={gltf.scene} dispose={null} />
    </group>
  );
};
